import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeroImage from "../../asset/Heroimage.png";
import { CanvasContext } from "../../contexts/CanvasContext";
import "./LandingHero.css";

const LandingHero = () => {
  const navigate = useNavigate();
  const { isDarkTheme } = useContext(CanvasContext);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const contentRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationFrameId;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();

    // Node class
    class Node {
      constructor(x, y) {
        this.x = x;
        this.y = y;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = 2;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(200, 200, 200, 0.5)";
        ctx.fill();
      }
    }

    // Create nodes
    const nodeCount = window.innerWidth <= 768 ? 50 : 100;

    const nodes = Array.from(
      { length: nodeCount },
      () =>
        new Node(Math.random() * canvas.width, Math.random() * canvas.height)
    );

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      nodes.forEach((node) => {
        node.update();
        node.draw();
      });

      // Draw lines between nearby nodes
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(200, 200, 200, ${0.4 - distance / 500})`;
            ctx.lineWidth = 1.04;
            ctx.stroke();
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Copy the ref value into a local variable
    const contentElement = contentRef.current;

    // Add intersection observer for scroll animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-rise");
          }
        });
      },
      { threshold: 0.1 }
    );

    if (contentElement) {
      observer.observe(contentElement);
    }

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
      if (contentElement) {
        observer.unobserve(contentElement);
      }
    };
  }, []);

  useEffect(() => {
    if (!hasAnimated) {
      const imageWrapper = imageRef.current;
      imageWrapper.classList.add("animate-image");
      imageWrapper.addEventListener("animationend", () => {
        setHasAnimated(true);
        imageWrapper.classList.remove("animate-image");
      });
    }
  }, [hasAnimated]);

  const handleMouseMove = (e) => {
    const { left, top, width, height } =
      imageRef.current.getBoundingClientRect();
    const x = (e.clientX - left) / width;
    const y = (e.clientY - top) / height;
    setMousePosition({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 0.5, y: 0.5 }); // Reset to center on mouse leave
  };

  const handleJoinClick = () => {
    navigate('/signin');
  };

  return (
    <section id="/" className={`hero ${isDarkTheme ? "hero--dark" : ""}`}>
      <canvas ref={canvasRef} className="hero__animated-background"></canvas>
      <div className="hero__container">
        <div className="hero__content" ref={contentRef}>
          <div className="hero__social-icons">
            <a
              href="https://www.facebook.com/prince.gondliya.5"
              className="hero__social-link"
              aria-label="Facebook"
            >
              <i className="fab fa-facebook-f"></i>
            </a>
            <a
              href="https://www.instagram.com/_prince_patel20/"
              className="hero__social-link"
              aria-label="Instagram"
            >
              <i className="fab fa-instagram"></i>
            </a>
            <a
              href="https://x.com/Princepatel2031"
              className="hero__social-link"
              aria-label="Twitter"
            >
              <i className="fab fa-twitter"></i>
            </a>
            <a
              href="https://youtube.com"
              className="hero__social-link"
              aria-label="YouTube"
            >
              <i className="fab fa-youtube"></i>
            </a>
          </div>
          <h1 className="hero__title">
            Excellent IT Solutions{" "}
            <span className="hero__title-highlight">For Your Success</span>
          </h1>
          <p className="hero__description">
            Empower your business with NexGen Sites, a revolutionary no-code web
            builder that allows you to create stunning websites effortlessly.
            Unlock endless customization options, access a comprehensive
            component library, and enjoy seamless site management across
            multiple projects. Whether you're a beginner or an experienced
            developer, NexGen Sites offers the flexibility and tools you need to
            bring your vision to life with ease and efficiency.
          </p>
          <button className="hero__cta-btn" onClick={handleJoinClick}>Join Now</button>
        </div>
        <div
          className="hero__image"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className="hero__image-wrapper"
            ref={imageRef}
            style={{
              transform: `perspective(1000px) rotateX(${
                (mousePosition.y - 0.5) * 10
              }deg) rotateY(${(mousePosition.x - 0.5) * -10}deg)`,
            }}
          >
            <img
              src={HeroImage}
              alt="IT Solutions Illustration"
              className="hero__image-content"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingHero;
